(() => {
  if (customElements.get('card-variant-picker')) return;

class CardVariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      /*
       * Product Recommendations in Horizon are hydrated after page load.
       * During a morph/hydration cycle the custom element can connect before
       * all of its child markup is available. Do not mark it initialized until
       * the embedded variant JSON is actually present.
       */
      const variantScript = this.querySelector('[data-cvp-variants]');

      if (!variantScript) {
        this.scheduleInitializationRetry();
        return;
      }

      this.initialized = true;
      this.handleClick = this.handleClick.bind(this);

      try {
        this.variants = JSON.parse(variantScript.textContent);
      } catch (error) {
        this.initialized = false;
        console.error('[Card variant picker] Invalid variant data', error);
        return;
      }

      this.colorIndex = Number(this.dataset.colorIndex);
      this.sizeIndex = Number(this.dataset.sizeIndex);
      this.sizeAction = this.dataset.sizeAction || 'select';

      this.colorButtons = Array.from(
        this.querySelectorAll('[data-cvp-color]')
      );

      this.sizeButtons = Array.from(
        this.querySelectorAll('[data-cvp-size]')
      );

      this.selectedColorElement = this.querySelector(
        '[data-selected-color]'
      );

      /*
       * IMPORTANT:
       * Do not use selected_or_first_available_variant here.
       * The customer must make the first visible selection.
       */
      this.selectedColor = null;
      this.selectedSize = null;

      /*
       * Capture phase makes the variant controls win over any product-card
       * click/navigation handler wrapping the recommendation card.
       */
      this.addEventListener('click', this.handleClick, true);

      this.resetInitialState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick, true);

      if (this.initializationFrame) {
        cancelAnimationFrame(this.initializationFrame);
        this.initializationFrame = null;
      }
    }

    scheduleInitializationRetry() {
      if (this.initializationFrame) return;

      let attempts = 0;
      const maxAttempts = 20;

      const retry = () => {
        this.initializationFrame = null;

        if (!this.isConnected || this.initialized) return;

        if (this.querySelector('[data-cvp-variants]')) {
          this.connectedCallback();
          return;
        }

        attempts += 1;

        if (attempts < maxAttempts) {
          this.initializationFrame = requestAnimationFrame(retry);
        }
      };

      this.initializationFrame = requestAnimationFrame(retry);
    }

    resetInitialState() {
      this.selectedColor = null;
      this.selectedSize = null;

      this.removeAttribute('data-selected-variant-id');

      this.colorButtons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });

      this.sizeButtons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });

      if (this.selectedColorElement) {
        this.selectedColorElement.textContent = '';
      }

      /*
       * On first load show every Color/Size that has at least one
       * available variant, but select nothing.
       */
      this.updateOptionAvailability();
      this.clearResolvedVariantFromCard();
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) return;

      const colorButton = event.target.closest('[data-cvp-color]');

      if (colorButton && this.contains(colorButton)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        this.selectColor(colorButton.dataset.colorValue, true);
        return;
      }

      const sizeButton = event.target.closest('[data-cvp-size]');

      if (!sizeButton || !this.contains(sizeButton)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const selectedVariant = this.selectSize(
        sizeButton.dataset.sizeValue,
        true
      );

      if (
        selectedVariant &&
        this.sizeAction === 'open' &&
        !window.Shopify?.designMode
      ) {
        window.location.assign(
          this.buildVariantUrl(selectedVariant.id)
        );
      }
    }

    selectColor(colorValue, updateCard) {
      if (!colorValue) return null;

      const availableColorVariants = this.variants.filter(
        (variant) =>
          variant.available &&
          variant.options?.[this.colorIndex] === colorValue
      );

      if (!availableColorVariants.length) return null;

      this.selectedColor = colorValue;

      this.colorButtons.forEach((button) => {
        const isSelected =
          button.dataset.colorValue === colorValue;

        button.classList.toggle('is-active', isSelected);
        button.setAttribute(
          'aria-pressed',
          isSelected ? 'true' : 'false'
        );
      });

      if (this.selectedColorElement) {
        this.selectedColorElement.textContent = colorValue;
      }

      /*
       * If a Size had already been selected first, keep it only when
       * that exact Color + Size combination is available.
       */
      let resolvedVariant = null;

      if (this.selectedSize) {
        resolvedVariant = availableColorVariants.find(
          (variant) =>
            variant.options?.[this.sizeIndex] === this.selectedSize
        ) || null;

        if (!resolvedVariant) {
          this.clearSizeSelection();
        }
      }

      this.updateOptionAvailability();

      if (resolvedVariant) {
        if (updateCard) {
          this.updateProductCard(resolvedVariant);
        }

        return resolvedVariant;
      }

      /*
       * Preserve the existing color-image behavior:
       * preview the first available media for this Color, but DO NOT
       * mark any Size as selected and DO NOT resolve the cart variant.
       */
      if (updateCard && availableColorVariants[0]) {
        this.previewProductCard(availableColorVariants[0]);
      }

      this.clearResolvedVariantFromCard();

      return null;
    }

    selectSize(sizeValue, updateCard) {
      if (!sizeValue) return null;

      const availableSizeVariants = this.variants.filter(
        (variant) =>
          variant.available &&
          variant.options?.[this.sizeIndex] === sizeValue
      );

      if (!availableSizeVariants.length) return null;

      this.selectedSize = sizeValue;

      this.sizeButtons.forEach((button) => {
        const isSelected =
          button.dataset.sizeValue === sizeValue &&
          !button.hidden &&
          !button.disabled;

        button.classList.toggle('is-active', isSelected);
        button.setAttribute(
          'aria-pressed',
          isSelected ? 'true' : 'false'
        );
      });

      /*
       * Size-first selection is allowed.
       * If Color is not chosen yet, keep the Size selected but wait
       * until the customer chooses Color before resolving a variant.
       */
      if (!this.selectedColor) {
        this.updateOptionAvailability();
        this.clearResolvedVariantFromCard();
        return null;
      }

      const matchingVariant = availableSizeVariants.find(
        (variant) =>
          variant.options?.[this.colorIndex] === this.selectedColor
      );

      if (!matchingVariant) {
        this.clearSizeSelection();
        this.updateOptionAvailability();
        this.clearResolvedVariantFromCard();
        return null;
      }

      this.updateOptionAvailability();

      if (updateCard) {
        this.updateProductCard(matchingVariant);
      }

      return matchingVariant;
    }

    clearSizeSelection() {
      this.selectedSize = null;

      this.sizeButtons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });
    }

    updateOptionAvailability() {
      /*
       * Colors:
       * - before Size selection: show colors having any available variant
       * - after Size selection: show only colors available in that Size
       */
      this.colorButtons.forEach((button) => {
        const colorValue = button.dataset.colorValue;

        const isAvailable = this.variants.some(
          (variant) =>
            variant.available &&
            variant.options?.[this.colorIndex] === colorValue &&
            (
              !this.selectedSize ||
              variant.options?.[this.sizeIndex] === this.selectedSize
            )
        );

        button.hidden = !isAvailable;
        button.disabled = !isAvailable;

        if (!isAvailable) {
          button.classList.remove('is-active');
          button.setAttribute('aria-pressed', 'false');
        }
      });

      /*
       * Sizes:
       * - before Color selection: show sizes having any available variant
       * - after Color selection: show only sizes available in that Color
       */
      this.sizeButtons.forEach((button) => {
        const sizeValue = button.dataset.sizeValue;

        const matchingVariant = this.variants.find(
          (variant) =>
            variant.available &&
            variant.options?.[this.sizeIndex] === sizeValue &&
            (
              !this.selectedColor ||
              variant.options?.[this.colorIndex] === this.selectedColor
            )
        );

        const isAvailable = Boolean(matchingVariant);

        button.hidden = !isAvailable;
        button.disabled = !isAvailable;

        if (matchingVariant) {
          button.dataset.variantId = String(matchingVariant.id);
        } else {
          delete button.dataset.variantId;
          button.classList.remove('is-active');
          button.setAttribute('aria-pressed', 'false');
        }
      });
    }

    getProductCard() {
      return (
        this.closest('product-card') ||
        this.closest('.resource-list__item')?.querySelector('product-card') ||
        null
      );
    }

    getVariantIdInputs(productCard = this.getProductCard()) {
      if (!productCard) return [];

      return Array.from(
        productCard.querySelectorAll(
          'product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]'
        )
      );
    }

    getAddToCartButtons(productCard = this.getProductCard()) {
      if (!productCard) return [];

      return Array.from(
        productCard.querySelectorAll(
          'product-form-component [ref="addToCartButton"], product-form-component button[type="submit"][name="add"]'
        )
      );
    }

    clearResolvedVariantFromCard() {
      const productCard = this.getProductCard();

      this.removeAttribute('data-selected-variant-id');

      if (!productCard) return;

      /*
       * Horizon renders the first available variant in the hidden
       * product-form input on the server. Clear it while Color + Size
       * are incomplete so the default variant cannot be added by mistake.
       */
      this.getVariantIdInputs(productCard).forEach((input) => {
        input.value = '';
      });

      this.getAddToCartButtons(productCard).forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      });
    }

    syncResolvedVariantToCard(variant) {
      const productCard = this.getProductCard();
      if (!productCard || !variant) return;

      const variantId = String(variant.id);

      /*
       * This is the critical cart fix: Horizon's buy-buttons form submits
       * the hidden input[name="id"]. Updating links/images alone does not
       * change the variant that /cart/add receives.
       */
      this.getVariantIdInputs(productCard).forEach((input) => {
        input.value = variantId;
        input.dispatchEvent(
          new Event('change', { bubbles: true })
        );
      });

      this.getAddToCartButtons(productCard).forEach((button) => {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      });

      /* Keep Horizon's add-to-cart animation/media in sync when possible. */
      if (variant.imageUrl) {
        productCard
          .querySelectorAll('add-to-cart-component')
          .forEach((component) => {
            component.dataset.productVariantMedia = variant.imageUrl;
          });
      }
    }

    previewProductCard(variant) {
      const productCard = this.closest('product-card');

      if (
        productCard &&
        variant.mediaId &&
        typeof productCard.previewVariant === 'function'
      ) {
        productCard.previewVariant(String(variant.mediaId));
      }

      if (productCard && variant.imageUrl) {
        productCard.setAttribute(
          'data-featured-media-url',
          variant.imageUrl
        );
      }
    }

    updateProductCard(variant) {
      const productCard = this.getProductCard();
      const variantId = String(variant.id);

      /*
       * Preserve the current Horizon integration from your existing file.
       */
      if (
        productCard &&
        typeof productCard.applyVariantToLinks === 'function'
      ) {
        productCard.applyVariantToLinks(variantId);
      } else {
        const productCardLink = productCard?.querySelector(
          '[ref="productCardLink"]'
        );

        if (productCardLink) {
          productCardLink.href =
            this.buildVariantUrl(variantId);
        }
      }

      this.previewProductCard(variant);
      this.syncResolvedVariantToCard(variant);

      this.dataset.selectedVariantId = variantId;
    }

    buildVariantUrl(variantId) {
      const productUrl = new URL(
        this.dataset.productUrl || window.location.pathname,
        window.location.origin
      );

      productUrl.searchParams.set('variant', variantId);

      return productUrl.toString();
    }
  }

  customElements.define(
    'card-variant-picker',
    CardVariantPicker
  );
})();